import { observable, runInAction } from 'mobx';
import { inject, observer } from 'mobx-react';
import * as React from 'react';
import { Grid, GridColumn } from 'telia-front-react';
import InternetCalculatorStore from '../../stores/InternetCalculatorStore';
import CalculatorCard from './card/CalculatorCard';
import { InternetCategory, ProductCard } from './InternetCalculatorInterfaces';
import InternetCategoriesSelector from './InternetCategoriesSelector';
import { addSpecialRequestCardWhenNecessary, getInternetCategories } from './InternetSpeedUtil';

interface Props {
  calculatorKey: string;
  recommendedInternets: string[];
  partner: boolean;
  isRecommendedInternet: (productOfferingCode: string) => boolean;
  internetCalculatorStore?: InternetCalculatorStore;
}

const getTabIndex = (internetSpeeds: ProductCard[], categories: InternetCategory[], suggestedInternets: string[]) => {
  const suggestedItem = internetSpeeds.filter((card) => suggestedInternets.includes(card.productOfferingCode))[0];
  let tabIndex = 0;
  categories.some((category, i) => {
    if (category.internets.includes(suggestedItem)) {
      tabIndex = i;
      return true;
    }
    return false;
  });
  return tabIndex;
};

@observer
class InternetCardsGrid extends React.Component<Props> {
  @observable accessor internetCategories: InternetCategory[] = [];

  public componentDidMount() {
    const { internetCalculatorStore, calculatorKey, recommendedInternets, partner } = this.props;
    const calculatorState = internetCalculatorStore.getState(calculatorKey);
    const internetSpeeds = partner ? internetCalculatorStore.partnerProducts(calculatorKey) : internetCalculatorStore.teliaProducts(calculatorKey);
    addSpecialRequestCardWhenNecessary(internetSpeeds, calculatorState.propState.specialRequestFormName, calculatorState.propState.segment);
    const internetSpeedTabs = internetCalculatorStore.getState(calculatorKey).propState.internetSpeedTabs;
    runInAction(() => {
      this.internetCategories = getInternetCategories(internetSpeeds, internetSpeedTabs);
    });

    if (internetSpeeds && recommendedInternets) {
      this.changeCategory(this.internetCategories[getTabIndex(internetSpeeds, this.internetCategories, recommendedInternets)]);
    } else if (this.internetCategories?.length) {
      this.changeCategory(this.internetCategories[0]);
    }
  }

  public componentDidUpdate(prevProps: Props) {
    const { internetCalculatorStore, calculatorKey, recommendedInternets, partner } = this.props;
    const internetSpeeds = partner ? internetCalculatorStore.partnerProducts(calculatorKey) : internetCalculatorStore.teliaProducts(calculatorKey);
    if (recommendedInternets && recommendedInternets !== prevProps.recommendedInternets && internetSpeeds) {
      this.changeCategory(this.internetCategories[getTabIndex(internetSpeeds, this.internetCategories, recommendedInternets)]);
    }
  }

  private changeCategory = (category: InternetCategory) => {
    const { partner, internetCalculatorStore } = this.props;
    internetCalculatorStore.selectCategory(category, partner);
  };

  public render() {
    const { internetCalculatorStore, calculatorKey, partner, isRecommendedInternet } = this.props;
    const calculatorState = internetCalculatorStore.getState(calculatorKey);
    const selectedAddress = calculatorState.address;
    const selectedRoom = calculatorState.room;
    const internetSpeeds = partner ? internetCalculatorStore.partnerInternets : internetCalculatorStore.teliaInternets;
    const someCardHasCampaign = internetSpeeds.some((c) => !!c.discountPrice?.amount);

    return (
      <>
        <InternetCategoriesSelector internetCategories={this.internetCategories} partner={partner} />
        <Grid equalHeight={true} align={['start-xs']}>
          {internetSpeeds.map((card: ProductCard, idx: number) => (
            <GridColumn key={`${partner}_grid_${idx}`} width={['xs-12', 'md-6', 'lg-3']}>
              <CalculatorCard
                calculatorKey={calculatorKey}
                segment={calculatorState.propState.segment}
                hideBetterInternetForms={calculatorState.propState.hideBetterInternetForms}
                card={card}
                partner={partner}
                key={card.uid}
                selectedRoom={selectedRoom}
                selectedAddress={selectedAddress}
                isRecommended={isRecommendedInternet(card.productOfferingCode)}
                specialRequestFormName={calculatorState.propState.specialRequestFormName}
                noEmptySpaceOnTop={!someCardHasCampaign}
              />
            </GridColumn>
          ))}
        </Grid>
      </>
    );
  }
}

export default inject('internetCalculatorStore')(InternetCardsGrid);
